import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

type MaterialType =
  | 'spider_banner'
  | 'roll_up'
  | 'flyer'
  | 'pliant'
  | 'caseta_luminoasa'
  | 'expozant'
  | 'cutie_personalizata'
  | 'altele'

interface MarketingMaterial {
  id: number
  type: MaterialType
  description: string | null
  qtyStock: number
  qtyMinAlert: number
}

const TYPE_LABELS: Record<MaterialType, string> = {
  spider_banner: 'Spider banner',
  roll_up: 'Roll-up',
  flyer: 'Flyer',
  pliant: 'Pliant',
  caseta_luminoasa: 'Casetă luminoasă',
  expozant: 'Expozant',
  cutie_personalizata: 'Cutie personalizată',
  altele: 'Altul',
}

export function MaterialeMarketingPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.materials:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<MarketingMaterial | null>(null)
  const [adjustTarget, setAdjustTarget] = useState<MarketingMaterial | null>(null)

  const query = useQuery<{ materials: MarketingMaterial[] }>({
    queryKey: ['marketing-materials'],
    queryFn: async () =>
      (await api.get<{ materials: MarketingMaterial[] }>('/marketing/materials')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<MarketingMaterial>) =>
      (await api.post('/marketing/materials', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-materials'] })
      toast.success('Material adăugat')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<MarketingMaterial> }) =>
      (await api.patch(`/marketing/materials/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-materials'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const adjustMutation = useMutation({
    mutationFn: async ({ id, delta }: { id: number; delta: number }) =>
      (await api.post(`/marketing/materials/${id}/adjust-stock`, { delta })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-materials'] })
      toast.success('Stoc actualizat')
      setAdjustTarget(null)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/materials/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-materials'] })
      toast.success('Șters')
    },
  })

  const materials = useMemo(() => query.data?.materials ?? [], [query.data])
  const pag = useClientPagination(materials, 10)
  const lowStockCount = materials.filter(
    (m) => m.qtyMinAlert > 0 && m.qtyStock <= m.qtyMinAlert,
  ).length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          Materiale marketing
          {lowStockCount > 0 && (
            <span className="ml-2 rounded bg-b4l-red-light px-2 py-0.5 text-xs text-b4l-red-dark">
              {lowStockCount} sub stoc minim
            </span>
          )}
        </h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Material nou</Button>}
      </div>

      <Card description="Stocuri materiale promo (spider banner, roll-up, flyer, casete luminoase). Configurează stoc minim → alertă în roșu pe Dashboard.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : materials.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun material. {canWrite && 'Apasă "+ Material nou".'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Tip</th>
                  <th className="py-2 pr-3 font-medium">Descriere</th>
                  <th className="py-2 pr-3 font-medium text-right">Stoc</th>
                  <th className="py-2 pr-3 font-medium text-right">Stoc minim</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((m) => {
                  const lowStock = m.qtyMinAlert > 0 && m.qtyStock <= m.qtyMinAlert
                  return (
                    <tr key={m.id} className="border-b border-b4l-border last:border-0">
                      <td className="py-2 pr-3 font-medium">{TYPE_LABELS[m.type]}</td>
                      <td className="py-2 pr-3 text-xs text-b4l-text-muted">
                        {m.description ?? '—'}
                      </td>
                      <td
                        className={`py-2 pr-3 text-right font-mono ${lowStock ? 'text-b4l-red-dark font-semibold' : ''}`}
                      >
                        {m.qtyStock}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-xs text-b4l-text-muted">
                        {m.qtyMinAlert > 0 ? m.qtyMinAlert : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {lowStock ? (
                          <span className="rounded bg-b4l-red-light px-2 py-0.5 text-[10px] text-b4l-red-dark">
                            ⚠ stoc redus
                          </span>
                        ) : (
                          <span className="rounded bg-b4l-green-light px-2 py-0.5 text-[10px] text-b4l-green-dark">
                            OK
                          </span>
                        )}
                      </td>
                      {canWrite && (
                        <td className="py-2 pr-3">
                          <div className="flex gap-1">
                            <Button
                              onClick={() => setAdjustTarget(m)}
                              className="!px-2 !py-1 !text-xs"
                            >
                              Ajustează stocul
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => setEditTarget(m)}
                              className="!px-2 !py-1 !text-xs"
                            >
                              Editează
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => {
                                if (confirm(`Ștergi ${TYPE_LABELS[m.type]}?`))
                                  deleteMutation.mutate(m.id)
                              }}
                              className="!px-2 !py-1 !text-xs"
                            >
                              Șterge
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="materiale"
          />
        </div>
      </Card>

      {createOpen && (
        <MaterialFormModal
          title="Material nou"
          submitLabel="Adaugă"
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <MaterialFormModal
          title={`Editează ${TYPE_LABELS[editTarget.type]}`}
          submitLabel="Salvează"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}

      {adjustTarget && (
        <AdjustStockModal
          material={adjustTarget}
          onClose={() => setAdjustTarget(null)}
          onSubmit={(delta) => adjustMutation.mutate({ id: adjustTarget.id, delta })}
          submitting={adjustMutation.isPending}
        />
      )}
    </div>
  )
}

interface FormProps {
  title: string
  submitLabel: string
  initial?: MarketingMaterial
  onClose: () => void
  onSubmit: (data: Partial<MarketingMaterial>) => void
  submitting: boolean
}

function MaterialFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [type, setType] = useState<MaterialType>(initial?.type ?? 'flyer')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [qtyStock, setQtyStock] = useState(initial?.qtyStock ?? 0)
  const [qtyMinAlert, setQtyMinAlert] = useState(initial?.qtyMinAlert ?? 0)

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                type,
                description: description.trim() || null,
                qtyStock,
                qtyMinAlert,
              })
            }
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Tip material</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as MaterialType)}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          >
            {Object.entries(TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Descriere"
          value={description ?? ''}
          onChange={(e) => setDescription(e.target.value)}
          hint="ex: Roll-up A4 - Boost4Life logo"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Stoc curent"
            type="number"
            value={qtyStock}
            onChange={(e) => setQtyStock(Number(e.target.value) || 0)}
          />
          <Field
            label="Stoc minim (alertă)"
            type="number"
            value={qtyMinAlert}
            onChange={(e) => setQtyMinAlert(Number(e.target.value) || 0)}
            hint="Sub această valoare → alertă"
          />
        </div>
      </div>
    </Modal>
  )
}

function AdjustStockModal({
  material,
  onClose,
  onSubmit,
  submitting,
}: {
  material: MarketingMaterial
  onClose: () => void
  onSubmit: (delta: number) => void
  submitting: boolean
}) {
  const [delta, setDelta] = useState(0)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Ajustează stoc: ${TYPE_LABELS[material.type]}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => onSubmit(delta)} loading={submitting} disabled={delta === 0}>
            Aplică
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p>
          Stoc curent: <span className="font-mono font-semibold">{material.qtyStock}</span>
        </p>
        <Field
          label="Delta (+ pentru intrare, − pentru consum)"
          type="number"
          value={delta}
          onChange={(e) => setDelta(Number(e.target.value) || 0)}
          hint="ex: +50 (recepție), -3 (consum)"
        />
        <p className="text-xs text-b4l-text-muted">
          Stoc nou: <span className="font-mono font-semibold">{material.qtyStock + delta}</span>
        </p>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
